import axios, { AxiosError, AxiosResponse } from "axios";

import { logoutAndRedirect } from "./auth";
import { ErrorHandler } from "./errorHandler";

const $axios = axios.create({
  baseURL:
    import.meta.env.VITE_WITH_PROXY === "true"
      ? "/api/admin"
      : `${import.meta.env.VITE_BASE_URL}/api/admin`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// This function is exported to be used by auth.ts
export const setAuthHeader = (token: string | null): void => {
  if (token) {
    $axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete $axios.defaults.headers.common.Authorization;
  }
};

// Request interceptor: This is a safety net to ensure the token is attached
// to every request if it exists, even if the initial setup in useAuth hasn't run yet.
$axios.interceptors.request.use(
  (config) => {
    // Check localStorage for token (atomWithStorage stores there)
    const token = localStorage.getItem("token");

    if (token && token !== "null") {
      // Remove quotes if they exist (JSON.parse artifact)
      const cleanToken = token.replace(/"/g, "");

      config.headers.Authorization = `Bearer ${cleanToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor: This handles automatic logout on 401 errors and shows error toasts.
$axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      logoutAndRedirect();
    } else {
      // Handle all other errors with ErrorHandler
      ErrorHandler.handle(error);
    }

    return Promise.reject(error);
  },
);

// ✅ We no longer call initializeAuth() here.
// The responsibility of setting the initial token is moved to the useAuth hook.

export default $axios;
